import { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  FormItem,
} from '@/features/trade/order/components/order-form-components';
import { Input } from '@/components/ui';
import { abbrFromSym } from '@/utils/token-symbol';
import {
  bigIntToDecimalStr,
  formatBigInt,
  parseDecimalToBigInt,
  validatedDecimalStr,
} from '@/utils/value-format';
import {
  COLLATERAL_DECIMALS,
  COLLATERAL_SYMBOL,
  useMarketStore,
} from '@/store/use-markets-store';
import { useFormContext } from 'react-hook-form';
import { CustomSlider } from './custom-slider';
import { rangeCount } from '../utils/helpers';
import { OrderFormSchema } from './order-form';
import { useTranslation } from 'react-i18next';
import { getSizeFromNumeraire } from '../utils/math';
import useMaxSize from '@/hooks/use-max-size';
import { ArrowLeftRight } from 'lucide-react';

type LastChangedInput = 'sizeInput' | 'sizePct';
type SizeUnit = typeof COLLATERAL_SYMBOL | 'token';

interface SizeInputProps {
  symbol: string;
  isSuccess?: boolean;
  hideBoxesMobile?: boolean;
  defaultSizePct?: number;
  autoSizePct?: boolean;
}

export default function SizeInput({
  symbol,
  defaultSizePct = 100,
  isSuccess,
  hideBoxesMobile,
  autoSizePct = true,
}: SizeInputProps) {
  const { t } = useTranslation();
  const form = useFormContext<OrderFormSchema>();

  const [sizeInput, setSizeInput] = useState<string>('');
  const [sizeUnit, setSizeUnit] = useState<SizeUnit>(COLLATERAL_SYMBOL);
  const [sizePct, setSizePct] = useState<number>(defaultSizePct);
  const [lastChangedInput, setLastChangedInput] =
    useState<LastChangedInput>('sizePct');

  const { price, maxSize } = useMaxSize(symbol);

  // input -> pct
  useEffect(() => {
    if (lastChangedInput !== 'sizeInput') return;
    let size = 0n;

    const { marketSpec } = useMarketStore(symbol).getState();

    switch (sizeUnit) {
      case COLLATERAL_SYMBOL:
        size = getSizeFromNumeraire(
          symbol,
          parseDecimalToBigInt(sizeInput, COLLATERAL_DECIMALS),
          price,
          false,
        );
        break;
      case 'token':
        size = parseDecimalToBigInt(sizeInput, marketSpec.sizeDecimals);
        break;
    }

    form.setValue(
      'order.size',
      bigIntToDecimalStr(size, marketSpec.sizeDecimals),
    );

    const sizePctBigInt = maxSize !== 0n ? (size * 10n ** 6n) / maxSize : 0n;
    const sizePctNum = Number(
      formatBigInt(sizePctBigInt, 4, { maxDigits: 1, commas: false }),
    );

    setSizePct(sizePctNum);
  }, [symbol, price, sizeInput, sizeUnit, maxSize, lastChangedInput]);

  const handleSizePctChange = useCallback(
    (sizePct: number) => {
      if (maxSize === 0n) {
        setSizeInput('');
        const { marketSpec } = useMarketStore(symbol).getState();
        form.setValue(
          'order.size',
          bigIntToDecimalStr(0n, marketSpec.sizeDecimals),
        );
      } else {
        const valBigInt = BigInt((sizePct * 10 ** 4).toFixed(0));
        const newSize = (valBigInt * maxSize) / 10n ** 6n;

        const { marketSpec } = useMarketStore(symbol).getState();

        const size = bigIntToDecimalStr(newSize, marketSpec.sizeDecimals);
        form.setValue('order.size', size);

        switch (sizeUnit) {
          case COLLATERAL_SYMBOL:
            setSizeInput(
              formatBigInt(
                newSize * price,
                marketSpec.sizeDecimals + marketSpec.priceDecimals,
                {
                  maxDigits: 2,
                  commas: false,
                },
              ),
            );
            break;
          case 'token':
            setSizeInput(size);
            break;
        }
      }
    },
    [symbol, maxSize, price, sizeUnit],
  );

  // pct -> input
  useEffect(() => {
    if (!autoSizePct || lastChangedInput === 'sizeInput' || sizePct <= 0)
      return;
    handleSizePctChange(sizePct);
  }, [handleSizePctChange, autoSizePct, sizePct, lastChangedInput]);

  const onSizeChange = (value: string) => {
    let validatedValue = value;
    switch (sizeUnit) {
      case COLLATERAL_SYMBOL:
        validatedValue = validatedDecimalStr(validatedValue, 2, 10);
        break;
      case 'token':
        const { marketSpec } = useMarketStore(symbol).getState();
        validatedValue = validatedDecimalStr(
          validatedValue,
          Number(marketSpec.sizeDecimals),
          10,
        );
        break;
    }
    setSizeInput(validatedValue);
    setLastChangedInput('sizeInput');
  };

  const onSizePctChange = (val: number) => {
    if (val > 100) return;
    setSizePct(val);
    setLastChangedInput('sizePct');
    if (!autoSizePct) {
      handleSizePctChange(val);
    }
  };

  const { isSubmitting } = form.formState;

  useEffect(() => {
    if (isSubmitting || !isSuccess) return;
    setSizeInput('');
    setSizePct(0);
  }, [isSubmitting, isSuccess]);

  return (
    <div className="mt-2">
      <FormItem className="h-10">
        <p className="text-vestgrey-100">{t('sizeInput.orderSize')}</p>
        <div className="flex" onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <Input
              className="max-w-[15ch] rounded-none bg-vestgrey-800 font-mono text-base text-foreground transition-colors hover:bg-border"
              onChange={(e) => onSizeChange(e.target.value)}
              value={sizeInput}
              autoComplete="off"
            />
          </FormControl>
          <div className="relative peer-disabled:opacity-60">
            <button
              type="button"
              className="flex h-10 w-full cursor-pointer items-center justify-between gap-2 bg-vestgrey-800 p-2 text-center font-mono transition-colors hover:bg-border"
              onClick={(e) => {
                e.preventDefault();
                setSizeUnit((prev) =>
                  prev === 'token' ? COLLATERAL_SYMBOL : 'token',
                );
              }}
            >
              <div className="relative flex items-center justify-end">
                <p className="opacity-0">
                  {COLLATERAL_SYMBOL.length > abbrFromSym(symbol).length
                    ? COLLATERAL_SYMBOL
                    : abbrFromSym(symbol)}
                </p>
                <span className="absolute text-vestgrey-50">
                  {sizeUnit === 'token'
                    ? abbrFromSym(symbol)
                    : COLLATERAL_SYMBOL}
                </span>
              </div>
              <ArrowLeftRight size={10} className="text-vestgrey-700" />
            </button>
          </div>
        </div>
      </FormItem>
      <FormItem className="flex-col items-stretch gap-2">
        <CustomSlider
          hideBoxesMobile={hideBoxesMobile}
          onChange={onSizePctChange}
          value={sizePct}
          minValue={0}
          maxValue={100}
          unitSymbol="%"
          buttonValues={rangeCount(25, 100, 4, 0)}
          step={1}
          disabled={maxSize === undefined}
        />
      </FormItem>
    </div>
  );
}

// const getLeverageRange = (maxLeverage: number) => {
//   let range: number[];
//   if (maxLeverage >= 100) {
//     range = [20, 40, 60, 80, 100];
//   } else if (maxLeverage >= 50) {
//     range = [10, 20, 30, 40, 50];
//   } else if (maxLeverage >= 20) {
//     range = [1, 5, 10, 15, 20];
//   } else if (maxLeverage >= 10) {
//     range = [1, 2, 5, 8, 10];
//   } else {
//     range = [1, 2, 3, 4, 5];
//   }
//   if (range[range.length - 1] !== maxLeverage) {
//     range[range.length - 1] = maxLeverage;
//   }
//   return range;
// };

// interface CollateralInputProps
//   extends Pick<SizeInputProps, 'symbol' | 'hideBoxesMobile'> {}

// export function CollateralInput({
//   symbol,
//   hideBoxesMobile,
// }: CollateralInputProps) {
//   const { t } = useTranslation();
//   const [collateralInput, setCollateralInput] = useState('');
//   const { markPrice, priceDecimals, sizeDecimals } = useStore(
//     useMarketStore(symbol),
//     (state) => ({
//       markPrice: state.marketData.markPrice,
//       priceDecimals: state.marketSpec.priceDecimals,
//       sizeDecimals: state.marketSpec.sizeDecimals,
//     }),
//   );

//   const { control, watch, setValue } = useFormContext<OrderFormSchema>();
//   const { isSubmitting, isSubmitSuccessful } = useFormState<OrderFormSchema>();

//   const orderType = watch('order.orderType');
//   const price =
//     orderType === OrderType.MARKET
//       ? markPrice
//       : parseDecimalToBigInt(watch('order.limitPrice'), priceDecimals);

//   useEffect(() => {
//     if (!isSubmitSuccessful || isSubmitting) return;
//     setCollateralInput('');
//   }, [isSubmitSuccessful, isSubmitting]);

//   useEffect(() => {
//     if (!price) return;
//     const newSize = bigIntToDecimalStr(
//       parseDecimalToBigInt(collateralInput, priceDecimals + sizeDecimals) /
//         price,
//       sizeDecimals,
//     );
//     setValue('order.size', newSize);
//   }, [collateralInput, price]);

//   return (
//     <div>
//       {/* <div className="flex items-end"> */}
//       {/* COLLATERAL */}
//       <FormField
//         control={control}
//         name="order.size"
//         render={({ field }) => (
//           <FormItem className="flex items-center">
//             <FormLabel>{t('sizeInput.youPay')}</FormLabel>
//             <div className="flex max-w-56">
//               <Input
//                 className="flex-1"
//                 value={collateralInput}
//                 onChange={(e) => setCollateralInput(e.target.value)}
//               />
//               <UnitSelect units={[COLLATERAL_SYMBOL]} />
//             </div>
//           </FormItem>
//         )}
//       />
//       <FormField
//         control={control}
//         name="order.size"
//         render={({ field }) => (
//           <FormItem className="my-2 flex flex-col items-start gap-3">
//             <FormLabel>{t('sizeInput.youReceive')}</FormLabel>
//             <div className="flex items-end gap-3">
//               <div className="flex flex-1">
//                 <Input
//                   className="peer"
//                   readOnly
//                   value={field.value}
//                   disabled
//                   // onChange={(e) => {
//                   //   setLastChangedInput('size');
//                   //   setSizeInput(e.target.value);
//                   //   field.onChange(
//                   //     bigIntToDecimalStr(
//                   //       parseDecimalToBigInt(e.target.value, sizeDecimals),
//                   //       sizeDecimals,
//                   //     ),
//                   //   );
//                   // }}
//                 />
//                 <UnitSelect
//                   defaultValue={abbrFromSym(symbol)}
//                   units={[abbrFromSym(symbol)]}
//                 />
//               </div>
//               <p className="mb-2 text-xl font-medium text-foreground">=</p>
//               <div className="flex flex-1">
//                 <Input
//                   className="peer"
//                   readOnly
//                   value={formatBigInt(
//                     parseDecimalToBigInt(field.value, sizeDecimals) * price,
//                     priceDecimals + sizeDecimals,
//                     { digits: 2, commas: false },
//                   )}
//                   disabled
//                   // onChange={(e) => {
//                   //   setLastChangedInput('size');
//                   //   setSizeInput(e.target.value);
//                   //   field.onChange(
//                   //     bigIntToDecimalStr(
//                   //       parseDecimalToBigInt(e.target.value, sizeDecimals),
//                   //       sizeDecimals,
//                   //     ),
//                   //   );
//                   // }}
//                 />
//                 <UnitSelect
//                   defaultValue={COLLATERAL_SYMBOL}
//                   units={[COLLATERAL_SYMBOL]}
//                 />
//               </div>
//             </div>
//           </FormItem>
//         )}
//       />
//     </div>
//   );
// }
